import { useEffect, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { api } from "@/lib/api";
import { slaInfo } from "@/lib/constants";
import { useStore } from "@/context/StoreContext";
import { Modal, readFileAsDataUrl } from "@/components/ui-z";

// Prioridade do chamado -> nomes possíveis da prioridade no Redmine (o idioma depende da instalação)
const PRIO_REDMINE = { baixa: ["Low", "Baixa"], media: ["Normal"], alta: ["High", "Alta"], critica: ["Urgent", "Urgente"] };
// Limite de anexo do Redmine
const MAX_ARQUIVO_MB = 5;
const MAX_BYTES = MAX_ARQUIVO_MB * 1024 * 1024;

// Data local (yyyy-mm-dd) para o input type="date"
const dataLocal = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Descrição inicial da tarefa: o chamado de origem (link de volta) + o que o solicitante escreveu
const descricaoInicial = (t) => [
  `*Chamado de origem:* ${t.codigo} - ${window.location.origin}/chamados/${t.id}`,
  `*Solicitante:* ${t.solicitante_nome}${t.solicitante_contato ? ` (${t.solicitante_contato})` : ""}`,
  t.categoria && `*Categoria:* ${t.categoria}`,
  "",
  t.descricao,
].filter((l) => typeof l === "string").join("\n");

const porNome = (lista, ...nomes) => lista.find((x) => nomes.some((n) => x.name.toLowerCase() === n?.toLowerCase()));

// Linha do formulário no estilo do Redmine: rótulo à direita, campo ao lado
const Campo = ({ id, label, required, children }) => (
  <div className="form-group row mb-2">
    <label htmlFor={id} className="col-sm-3 col-form-label col-form-label-sm text-sm-right font-weight-bold">
      {label}{required && <span className="text-danger"> *</span>}
    </label>
    <div className="col-sm-9">{children}</div>
  </div>
);

const Lista = ({ id, value, onChange, itens, carregando, vazio = "— Selecione —", ...props }) => (
  <select id={id} className="form-control form-control-sm" value={value} disabled={carregando} onChange={(e) => onChange(e.target.value)} {...props}>
    <option value="">{carregando ? "Carregando..." : vazio}</option>
    {itens.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
  </select>
);

// Campo personalizado obrigatório do projeto (ex.: Reincidência): lista curta em botões de opção, longa em select,
// o resto em texto
const CampoPersonalizado = ({ c, value, onChange }) => {
  const id = `rm-cf-${c.id}`;
  if (c.format === "list" && c.valores.length <= 4) return (
    <div className="border rounded px-2 py-1" data-testid={`redmine-cf-${c.id}`}>
      {c.valores.map((v, i) => (
        <div key={v} className="custom-control custom-radio">
          <input id={`${id}-${i}`} type="radio" name={id} className="custom-control-input" value={v} checked={value === v} onChange={() => onChange(v)} />
          <label htmlFor={`${id}-${i}`} className="custom-control-label font-weight-normal">{v}</label>
        </div>
      ))}
    </div>
  );
  if (c.format === "list") return (
    <select id={id} data-testid={`redmine-cf-${c.id}`} className="form-control form-control-sm" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— Selecione —</option>
      {c.valores.map((v) => <option key={v} value={v}>{v}</option>)}
    </select>
  );
  return <input id={id} data-testid={`redmine-cf-${c.id}`} className="form-control form-control-sm" value={value} onChange={(e) => onChange(e.target.value)} />;
};

export default function RedmineModal({ t, onClose }) {
  const { sendToRedmine } = useStore();
  const modal = useRef(null);
  // Listas do Redmine (GET /api/redmine/options); null = carregando
  const [opcoes, setOpcoes] = useState(null);
  // Tipos, campos personalizados obrigatórios e versões do projeto escolhido
  const [projeto, setProjeto] = useState({ tipos: [], campos: [], versoes: [] });
  const [carregandoProjeto, setCarregandoProjeto] = useState(false);
  // Início = abertura do chamado; data prevista = prazo (SLA) do chamado
  const [f, setF] = useState(() => ({
    projeto: "", tipo: "", titulo: t.titulo, descricao: descricaoInicial(t), situacao: "", prioridade: "", versao: "",
    inicio: dataLocal(t.created_at), data_prevista: dataLocal(slaInfo(t).deadline),
  }));
  const set = (k) => (v) => setF((atual) => ({ ...atual, [k]: v }));
  // Valores dos campos personalizados, por id
  const [campos, setCampos] = useState({});
  // Evidências do chamado que vão como anexo (todas marcadas, menos as acima do limite) + arquivos escolhidos aqui
  const [evidencias, setEvidencias] = useState(() => new Set(t.evidencias.filter((ev) => !(ev.size > MAX_BYTES)).map((ev) => ev.id)));
  const [arquivos, setArquivos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  // Resposta do "Sugerir com IA" (recomendação, justificativa e tarefas abertas parecidas)
  const [sugestao, setSugestao] = useState(null);
  const [sugerindo, setSugerindo] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get("/redmine/options").then(({ data }) => {
      if (!vivo) return;
      setOpcoes(data);
      // Padrões: primeira situação (normalmente "New") e a prioridade equivalente à do chamado
      const prio = porNome(data.prioridades, ...(PRIO_REDMINE[t.prioridade] || []))?.id ?? data.prioridade_padrao ?? "";
      setF((atual) => ({ ...atual, situacao: atual.situacao || String(data.situacoes[0]?.id ?? ""), prioridade: atual.prioridade || String(prio) }));
    }).catch((e) => {
      if (!vivo) return;
      setOpcoes({ erro: e.response?.data?.detail || "Não foi possível carregar os dados do Redmine." });
    });
    return () => { vivo = false; };
  }, [t.prioridade]);

  // Ao trocar de projeto: mantém o tipo se ele existir no novo projeto (senão sugere "Feature") e limpa a versão
  useEffect(() => {
    setF((atual) => ({ ...atual, versao: "" }));
    if (!f.projeto) { setProjeto({ tipos: [], campos: [], versoes: [] }); return undefined; }
    let vivo = true;
    setCarregandoProjeto(true);
    api.get(`/redmine/projects/${f.projeto}/fields`).then(({ data }) => {
      if (!vivo) return;
      setProjeto(data);
      setF((atual) => ({ ...atual, tipo: data.tipos.some((x) => String(x.id) === atual.tipo) ? atual.tipo : String(porNome(data.tipos, "Feature")?.id ?? "") }));
    }).catch(() => vivo && toast.error("Não foi possível carregar os dados do projeto.")).finally(() => vivo && setCarregandoProjeto(false));
    return () => { vivo = false; };
  }, [f.projeto]);

  // Campos obrigatórios que valem para o tipo escolhido (lista de tipos vazia = todos)
  const camposDoTipo = f.tipo ? projeto.campos.filter((c) => !c.trackers.length || c.trackers.includes(Number(f.tipo))) : [];

  // Reincidência sugerida pela IA: o campo só aparece depois que os dados do projeto chegam
  useEffect(() => {
    if (!sugestao?.reincidencia) return;
    const campo = projeto.campos.find((c) => /reincid/i.test(c.name) && c.valores.includes(sugestao.reincidencia));
    if (campo) setCampos((atual) => (atual[campo.id] ? atual : { ...atual, [campo.id]: sugestao.reincidencia }));
  }, [projeto.campos, sugestao]);

  // IA (workflow do n8n) compara o chamado com as tarefas abertas e preenche os campos; o atendente revisa antes de criar
  const sugerir = async () => {
    setSugerindo(true);
    try {
      const { data } = await api.post(`/tickets/${t.id}/redmine/sugestao`, {
        project_id: f.projeto ? Number(f.projeto) : null, link: `${window.location.origin}/chamados/${t.id}`,
      });
      setSugestao(data);
      setF((atual) => ({
        ...atual,
        projeto: data.projeto_id ? String(data.projeto_id) : atual.projeto,
        tipo: data.tipo_id ? String(data.tipo_id) : atual.tipo,
        prioridade: data.prioridade_id ? String(data.prioridade_id) : atual.prioridade,
        titulo: data.titulo || atual.titulo,
        descricao: data.descricao || atual.descricao,
      }));
      toast.success("Campos preenchidos pela IA. Revise antes de criar.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível obter a sugestão da IA.");
    } finally {
      setSugerindo(false);
    }
  };

  const alternarEvidencia = (evId) => setEvidencias((atual) => {
    const s = new Set(atual);
    if (s.has(evId)) s.delete(evId); else s.add(evId);
    return s;
  });
  const adicionarArquivos = (lista) => {
    const ok = [...lista].filter((a) => {
      if (a.size <= MAX_BYTES) return true;
      toast.warning(`"${a.name}" tem mais de ${MAX_ARQUIVO_MB} MB (limite do Redmine).`);
      return false;
    });
    setArquivos((atual) => [...atual, ...ok]);
  };

  const carregando = opcoes === null;
  const desligado = opcoes && !opcoes.erro && !opcoes.configurado;
  const indisponivel = carregando || desligado || !!opcoes?.erro;
  const prazoInvalido = f.data_prevista && f.inicio && f.data_prevista < f.inicio;
  const faltando = [
    !f.projeto && "Projeto", !f.tipo && "Tipo", !f.titulo.trim() && "Título", !f.situacao && "Situação", !f.prioridade && "Prioridade",
    ...camposDoTipo.filter((c) => !campos[c.id]?.trim()).map((c) => c.name),
  ].filter(Boolean);

  const enviar = async () => {
    if (faltando.length) return toast.warning(`Preencha: ${faltando.join(", ")}.`);
    if (prazoInvalido) return toast.warning("A data prevista não pode ser antes do início.");
    setEnviando(true);
    try {
      let novos;
      try { novos = await Promise.all(arquivos.map(readFileAsDataUrl)); }
      catch { return toast.error("Não foi possível ler um dos arquivos."); }
      const res = await sendToRedmine(t.id, {
        project_id: Number(f.projeto), tracker_id: Number(f.tipo), status_id: Number(f.situacao), priority_id: Number(f.prioridade),
        subject: f.titulo.trim(), description: f.descricao, fixed_version_id: f.versao ? Number(f.versao) : null,
        start_date: f.inicio || null, due_date: f.data_prevista || null,
        custom_fields: camposDoTipo.map((c) => ({ id: c.id, value: campos[c.id].trim() })),
        evidencias_ids: [...evidencias], arquivos: novos,
      });
      if (!res) return;
      toast.success(`Tarefa #${res.id} criada no Redmine`);
      modal.current.hide(() => window.open(res.url, "_blank", "noopener"));
    } finally {
      setEnviando(false);
    }
  };

  const id = (k) => `rm-${k}`;
  return (
    <Modal
      ref={modal} testId="redmine-modal" size="modal-lg" onClose={onClose}
      title={<><i className="fas fa-share-square mr-2" />Enviar para o Redmine &middot; {t.codigo}</>}
      footer={<>
        <button type="button" className="btn btn-default" data-dismiss="modal">Cancelar</button>
        <button type="button" data-testid="redmine-confirm-btn" className="btn btn-danger" disabled={enviando || indisponivel} onClick={enviar}>
          {enviando ? <i className="fas fa-spinner fa-spin mr-1" /> : <i className="fas fa-share-square mr-1" />}Criar tarefa no Redmine
        </button>
      </>}
    >
      {desligado && (
        <div className="alert alert-warning" data-testid="redmine-off">
          <i className="fas fa-plug mr-1" />Integração com o Redmine desligada: configure <code>Redmine:ApiKey</code> na API.
        </div>
      )}
      {opcoes?.erro && <div className="alert alert-danger" data-testid="redmine-error"><i className="fas fa-exclamation-triangle mr-1" />{opcoes.erro}</div>}

      {opcoes?.sugestao && (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <small className="text-muted">A IA compara o chamado com as tarefas abertas no Redmine e preenche os campos.</small>
          <button type="button" data-testid="redmine-ia-btn" className="btn btn-sm btn-outline-primary text-nowrap ml-2" disabled={sugerindo || enviando} onClick={sugerir}>
            {sugerindo ? <><i className="fas fa-spinner fa-spin mr-1" />Analisando...</> : <><i className="fas fa-magic mr-1" />{sugestao ? "Sugerir de novo" : "Sugerir com IA"}</>}
          </button>
        </div>
      )}
      {sugestao && (
        <div className={`callout ${sugestao.recomendacao === "vincular_existente" ? "callout-warning" : "callout-info"} py-2`} data-testid="redmine-ia-result">
          {sugestao.recomendacao === "vincular_existente" ? (
            <p className="mb-1">
              <i className="fas fa-exclamation-triangle text-warning mr-1" /><b>Já existe uma tarefa aberta para isso: </b>
              <a href={sugestao.tarefa_existente_url} target="_blank" rel="noopener noreferrer">#{sugestao.tarefa_existente_id}</a>.
              Avalie usar essa tarefa em vez de criar outra.
            </p>
          ) : <p className="mb-1"><i className="fas fa-magic text-primary mr-1" /><b>Sugestão: criar uma nova tarefa.</b></p>}
          {sugestao.justificativa && <p className="text-sm mb-1">{sugestao.justificativa}</p>}
          {sugestao.tarefas_relacionadas?.length > 0 && (
            <>
              <small className="text-muted d-block">Tarefas abertas parecidas</small>
              <ul className="list-unstyled text-sm mb-0">
                {sugestao.tarefas_relacionadas.map((r) => (
                  <li key={r.id}>
                    <span className={`badge mr-1 ${r.similaridade === "alta" ? "badge-danger" : r.similaridade === "media" ? "badge-warning" : "badge-secondary"}`}>{r.similaridade}</span>
                    <a href={r.url} target="_blank" rel="noopener noreferrer">#{r.id}</a> {r.titulo}
                    <span className="text-muted"> · {r.projeto} · {r.situacao}</span>
                    <div className="text-muted font-italic">{r.motivo}</div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <Campo id={id("projeto")} label="Projeto" required>
        <select id={id("projeto")} data-testid="redmine-projeto" className="form-control form-control-sm" value={f.projeto}
          disabled={indisponivel} onChange={(e) => set("projeto")(e.target.value)}>
          <option value="">{carregando ? "Carregando..." : "— Selecione —"}</option>
          {(opcoes?.projetos || []).map((p) => (
            <option key={p.id} value={p.id}>{"   ".repeat(p.nivel)}{p.nivel ? "» " : ""}{p.name}</option>
          ))}
        </select>
      </Campo>
      <Campo id={id("tipo")} label="Tipo" required>
        <Lista id={id("tipo")} data-testid="redmine-tipo" value={f.tipo} onChange={set("tipo")} itens={projeto.tipos}
          carregando={carregandoProjeto} disabled={!f.projeto || carregandoProjeto} />
        {!f.projeto && <small className="text-muted">Escolha o projeto para ver os tipos e as versões dele.</small>}
      </Campo>
      <Campo id={id("titulo")} label="Título" required>
        <input id={id("titulo")} data-testid="redmine-titulo" className="form-control form-control-sm" maxLength={255}
          value={f.titulo} onChange={(e) => set("titulo")(e.target.value)} />
      </Campo>
      <Campo id={id("descricao")} label="Descrição">
        <textarea id={id("descricao")} data-testid="redmine-descricao" className="form-control form-control-sm" rows={7}
          value={f.descricao} onChange={(e) => set("descricao")(e.target.value)} />
        <small className="text-muted">Formatação do Redmine (Textile): <code>*negrito*</code>, <code>_itálico_</code>, <code>* item</code>.</small>
      </Campo>
      <Campo id={id("situacao")} label="Situação" required>
        <Lista id={id("situacao")} value={f.situacao} onChange={set("situacao")} itens={opcoes?.situacoes || []} carregando={carregando} />
      </Campo>
      <Campo id={id("prioridade")} label="Prioridade" required>
        <Lista id={id("prioridade")} data-testid="redmine-prioridade" value={f.prioridade} onChange={set("prioridade")}
          itens={opcoes?.prioridades || []} carregando={carregando} />
      </Campo>
      <Campo id={id("versao")} label="Versão">
        <Lista id={id("versao")} data-testid="redmine-versao" value={f.versao} onChange={set("versao")} itens={projeto.versoes}
          carregando={carregandoProjeto} disabled={!f.projeto || carregandoProjeto}
          vazio={f.projeto && !projeto.versoes.length ? "— Projeto sem versões abertas —" : "— Nenhuma —"} />
      </Campo>
      <Campo id={id("inicio")} label="Início">
        <input id={id("inicio")} type="date" className="form-control form-control-sm" style={{ maxWidth: 180 }} value={f.inicio} onChange={(e) => set("inicio")(e.target.value)} />
        <small className="text-muted">Abertura do chamado.</small>
      </Campo>
      <Campo id={id("data_prevista")} label="Data prevista">
        <input id={id("data_prevista")} type="date" className={`form-control form-control-sm ${prazoInvalido ? "is-invalid" : ""}`} style={{ maxWidth: 180 }}
          min={f.inicio || undefined} value={f.data_prevista} onChange={(e) => set("data_prevista")(e.target.value)} />
        <small className="text-muted">Prazo (SLA) do chamado.</small>
      </Campo>
      {camposDoTipo.map((c) => (
        <Campo key={c.id} id={`rm-cf-${c.id}`} label={c.name} required>
          <CampoPersonalizado c={c} value={campos[c.id] || ""} onChange={(v) => setCampos((atual) => ({ ...atual, [c.id]: v }))} />
        </Campo>
      ))}
      <Campo id={id("arquivos")} label="Arquivos">
        {t.evidencias.length > 0 && (
          <div className="mb-2" data-testid="redmine-evidencias">
            <small className="text-muted d-block mb-1">Evidências do chamado</small>
            {t.evidencias.map((ev) => {
              const grande = ev.size > MAX_BYTES;
              return (
                <div key={ev.id} className="custom-control custom-checkbox">
                  <input id={id(`ev-${ev.id}`)} type="checkbox" className="custom-control-input" disabled={grande}
                    checked={evidencias.has(ev.id)} onChange={() => alternarEvidencia(ev.id)} />
                  <label htmlFor={id(`ev-${ev.id}`)} className="custom-control-label font-weight-normal text-sm">
                    {ev.name}{grande && <span className="badge badge-warning ml-1">maior que {MAX_ARQUIVO_MB} MB</span>}
                  </label>
                </div>
              );
            })}
          </div>
        )}
        <label className="btn btn-xs btn-default mb-1">
          <i className="fas fa-paperclip mr-1" />Escolher arquivos
          <input id={id("arquivos")} type="file" multiple hidden data-testid="redmine-arquivos" onChange={(e) => { adicionarArquivos(e.target.files); e.target.value = ""; }} />
        </label>
        <small className="text-muted ml-2">(Tamanho máximo: {MAX_ARQUIVO_MB} MB)</small>
        {arquivos.map((a, i) => (
          <div key={`${a.name}-${i}`} className="text-sm">
            <i className="fas fa-file text-muted mr-1" />{a.name}
            <button type="button" className="btn btn-tool py-0" title="Remover" onClick={() => setArquivos((atual) => atual.filter((_, j) => j !== i))}><i className="fas fa-times" /></button>
          </div>
        ))}
      </Campo>
    </Modal>
  );
}
